/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description ProviderNpis
 */
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Row, Select, Col } from 'antd';
import { Link } from 'react-router-dom';

import Button from 'components/widgets/button';
import usePaginatedProviders from 'hooks/admin/usePaginatedProviders';
import { ENROLLMENT_REQUIRED_CODES, PAYER_ENABLED_CODES } from 'utils/eligibility/eligibilityRequestForm';
import { getEditPayerDetailsPath } from 'utils/router';
import { getProviderMetaData } from 'utils/metadata';
import { ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES } from 'app-constants/eligibility';

const cacheKey = 'eligibility-npi-select-field';

const getProviderNameLabel = (registeredName: string | undefined, npi?: string): string => {
	if (!npi) {
		return registeredName;
	}
	return `${registeredName} (NPI : ${npi})`;
};

const getOptions = (providerMeta: Metadata.IProviderMetaObj, payersMeta: Payers.IPayerMeta, tenantPayerId: string) => {
	const filteredProviders = _.filter(payersMeta?.providerPayerMeta?.data || [], ({ providerId }) => {
		const providerData = _.get(providerMeta, providerId);
		return providerData?.isActive;
	});
	const validProviders = _.filter(
		filteredProviders,
		({ providerId, enrollmentStatus }) =>
			_.get(providerMeta, providerId)?.canMakeRequests && _.includes(PAYER_ENABLED_CODES, enrollmentStatus),
	);
	const ekycInCompletedProviders = _.filter(filteredProviders, ({ providerId }) => !_.get(providerMeta, providerId)?.isEkycCompleted);

	const nonEnrolledProviders = _.filter(
		filteredProviders,
		({ providerId, enrollmentStatus }) =>
			_.get(providerMeta, providerId)?.isEkycCompleted && _.includes(ENROLLMENT_REQUIRED_CODES, enrollmentStatus),
	);

	const validProviderOptions = _.map(validProviders, ({ providerId }) => {
		const provider = _.get(providerMeta, providerId);

		return {
			searchLabel: getProviderNameLabel(provider?.name, provider?.npi),
			label: getProviderNameLabel(provider?.name, provider?.npi),
			value: providerId,
		};
	});

	const additionalOptions = [] as AnyType;
	if (!_.isEmpty(ekycInCompletedProviders)) {
		additionalOptions.push({
			label: 'Identity verification required',
			options: _.map(ekycInCompletedProviders, ({ providerId }) => {
				const provider = _.get(providerMeta, providerId);
				return {
					value: providerId,
					disabled: true,
					searchLabel: getProviderNameLabel(provider?.name, provider?.npi),
					label: (
						<Row gutter={16}>
							<Col span={20} style={{ margin: 'auto 0px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{getProviderNameLabel(provider?.name, provider?.npi)}
							</Col>
							<Col span={4}>
								<Button.IdentityVerification
									forDropdown
									buttonProps={{ type: 'link' }}
									entity={_.get(providerMeta, providerId)?.entity}
								/>
							</Col>
						</Row>
					),
				};
			}),
		});
	}
	if (!_.isEmpty(nonEnrolledProviders)) {
		additionalOptions.push({
			label: 'Enrollment required',
			options: _.map(nonEnrolledProviders, ({ providerId, enrollmentStatus }) => {
				const provider = _.get(providerMeta, providerId);
				return {
					value: providerId,
					disabled: true,
					searchLabel: getProviderNameLabel(provider?.name, provider?.npi),
					label: (
						<Row gutter={16}>
							<Col span={16} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{getProviderNameLabel(provider?.name, provider?.npi)}
							</Col>
							<Col span={6}>
								{enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested && 'In Progress'}
								{enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled && (
									<Link to={getEditPayerDetailsPath(tenantPayerId)}>{'Enroll Now'}</Link>
								)}
							</Col>
						</Row>
					),
				};
			}),
		});
	}
	return [...validProviderOptions, ...additionalOptions];
};

export interface IProps {
	value: string;
	disabled?: boolean;
	allowClear?: boolean;
	onChange: (value: string) => void;
	payersMeta: Payers.IPayerMeta;
	tenantPayerId: string;
}

/**
 * Provider Groups
 * @param {IProps} props
 * @returns {React.FC} Provider Groups
 */
const ProviderNpis: React.FC<IProps> = ({ value, disabled, allowClear, payersMeta, onChange, tenantPayerId }) => {
	const [npiNumber, setNpiNumber] = useState(value);
	const { data, isLoading } = usePaginatedProviders({ allProviders: true }, { refetchInterval: 15000 }, cacheKey);
	const providerMeta = useMemo(() => getProviderMetaData(data?.data || []), [data]);

	useEffect(() => {
		if (value !== npiNumber) {
			setNpiNumber(value);
		}
	}, [value]);

	const sendDateToProps = (newNpi: string) => {
		setNpiNumber(newNpi);
		onChange(newNpi);
	};

	return (
		<Select
			showSearch
			disabled={disabled}
			allowClear={allowClear}
			loading={isLoading}
			placeholder='Please Select a Provider/Organization'
			value={npiNumber || undefined}
			onChange={(newValue) => sendDateToProps(newValue)}
			optionFilterProp='searchLabel'
			options={getOptions(providerMeta, payersMeta, tenantPayerId)}
		/>
	);
};

export default ProviderNpis;
